// Companies > detail > Payouts > Commission (issue #370, Paystack plan 6.11).
// What Zuttle keeps from this operator's bookings: the free trial, founding or
// standard, a super admin's own rate, and the history of changes. Any admin
// reads; only a super admin changes it (the server answers 403 otherwise).
// A change applies to bookings made after it, never to one already made.

import React, { useEffect, useState } from 'react';
import { Percent } from 'lucide-react';
import { AdminCompanyAPI, APIUtils } from '../../services/api';
import { formatDateTime } from './payoutLabels';

const percent = (bps) => `${Number((bps / 100).toFixed(2))}%`;
const rand = (cents) => `R${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

const RULE_LABEL = { trial: 'Free trial', founding: 'Founding', standard: 'Standard', override: 'Own rate (super admin)' };
const ACTION_LABEL = { set_founding: 'Founding operator', set_override: 'Own rate', extend_founding: 'Founding term' };

const describeRule = (rule) => {
  const fee = rule.bearer === 'account' ? 'Zuttle bears the Paystack fee.' : 'The operator bears the Paystack fee.';
  const vat = rule.vatRateBps ? ` Plus VAT at ${percent(rule.vatRateBps)}.` : '';
  if (rule.rule === 'trial') return `${rand(rule.flatCents)} per booking. ${fee}${vat}`;
  return `${percent(rule.rateBps)} per booking, at least ${rand(rule.minCents)}. ${fee}${vat}`;
};

const describeTrial = (trial) => {
  if (!trial.startedAt) return `Not started. It starts at the first booking: ${trial.days} days or ${trial.maxBookings} bookings.`;
  const count = `${trial.bookings} of ${trial.maxBookings} bookings`;
  if (trial.open) return `${count}, started ${formatDateTime(trial.startedAt)}, ends by ${formatDateTime(trial.endsAt)} at the latest.`;
  const by = trial.endedBy === 'bookings' ? `after ${trial.maxBookings} bookings` : `after ${trial.days} days`;
  return `Ended ${by}, on ${formatDateTime(trial.endsAt)} (${count}).`;
};

const describeChange = (entry) => {
  const show = (v) => {
    if (entry.action === 'set_override') return v === null || v === undefined ? 'none' : percent(v);
    if (entry.action === 'extend_founding') return `${v} months`;
    return v ? 'yes' : 'no';
  };
  return `${show(entry.from)} to ${show(entry.to)}`;
};

const Field = ({ label, children }) => (
  <div>
    <dt className="text-gray-500">{label}</dt>
    <dd className="mt-0.5 font-medium text-gray-900">{children}</dd>
  </div>
);

/**
 * The super admin's form. Sends only what changed, with the note and the
 * version it loaded: if someone else changed the commission since, the
 * server refuses (409) and changes nothing.
 */
const CommissionForm = ({ data, onSave }) => {
  const [founding, setFounding] = useState(data.founding);
  const [rate, setRate] = useState(data.overrideBps === null ? '' : String(data.overrideBps / 100));
  const [months, setMonths] = useState(data.foundingMonths);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    const body = { note, version: data.version };
    if (founding !== data.founding) body.founding = founding;
    const bps = rate.trim() === '' ? null : Math.round(Number(rate) * 100);
    if (rate.trim() !== '' && (!Number.isFinite(Number(rate)) || Math.abs(Number(rate) * 100 - bps) > 1e-9)) {
      setError('Give the rate in percent with at most two decimals, for example 12.5');
      return;
    }
    if (bps !== data.overrideBps) body.overrideBps = bps;
    if (Number(months) !== data.foundingMonths) body.foundingMonths = Number(months);
    setBusy(true);
    setError(null);
    try {
      await onSave(body);
      setNote('');
    } catch (err) {
      setError(APIUtils.handleError(err).message || 'Could not save the commission');
    } finally {
      setBusy(false);
    }
  };

  const monthOptions = [];
  for (let m = data.foundingMonths; m <= data.foundingMaxMonths; m += 1) monthOptions.push(m);

  return (
    <form onSubmit={save} className="mt-6 border-t border-gray-100 pt-4 space-y-3 text-sm">
      <h3 className="font-semibold text-gray-700">Change (super admin)</h3>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={founding} onChange={(e) => setFounding(e.target.checked)} />
        Founding operator ({percent(data.foundingBps)} after the trial)
      </label>
      <label className="block">
        <span className="text-gray-600">Founding term after the trial</span>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="mt-1 block border border-gray-300 rounded-md px-2 py-1">
          {monthOptions.map((m) => <option key={m} value={m}>{m} months</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-gray-600">Own rate in percent, after the trial (empty for none)</span>
        <input aria-label="Own rate in percent" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="none" inputMode="decimal"
          className="mt-1 block w-32 border border-gray-300 rounded-md px-2 py-1" />
      </label>
      <label className="block">
        <span className="text-gray-600">Why (kept in the history)</span>
        <textarea aria-label="Why" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500}
          className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1" />
      </label>
      {error && <p className="text-red-600">{error}</p>}
      <button type="submit" disabled={busy || !note.trim()}
        className="px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
        {busy ? 'Saving...' : 'Save commission'}
      </button>
    </form>
  );
};

const CompanyCommission = ({ companyId, isSuperAdmin = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    AdminCompanyAPI.getCommission(companyId)
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => { if (!cancelled) setError(APIUtils.handleError(err).message || 'Could not load the commission'); });
    return () => { cancelled = true; };
  }, [companyId]);

  const save = async (body) => {
    const res = await AdminCompanyAPI.updateCommission(companyId, body);
    setData(res.data.data);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Percent size={18} className="text-emerald-600" />
        <h2 className="text-lg font-semibold text-gray-900">Commission</h2>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="text-sm text-gray-500">Loading...</p>}

      {data && (
        <>
          <p className="text-sm text-gray-900">
            <span className="font-semibold">Now: {RULE_LABEL[data.current.rule] || data.current.rule}.</span> {describeRule(data.current)}
          </p>
          <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Field label="Free trial">{describeTrial(data.trial)}</Field>
            <Field label="Founding operator">
              {data.founding
                ? `Yes: ${percent(data.foundingBps)} for ${data.foundingMonths} months after the trial${data.foundingEndsAt ? `, to ${formatDateTime(data.foundingEndsAt)}` : ''}`
                : `No: ${percent(data.standardBps)} after the trial`}
            </Field>
            <Field label="Own rate">{data.overrideBps === null ? 'None' : `${percent(data.overrideBps)}, at least ${rand(data.minCents)}`}</Field>
            <Field label="VAT on commission">{data.current.vatRateBps ? percent(data.current.vatRateBps) : 'Off (Zuttle is not a VAT vendor)'}</Field>
          </dl>
          <p className="mt-3 text-xs text-gray-500">Each booking keeps the commission it was made with. A change here applies to bookings made after it.</p>

          {isSuperAdmin && <CommissionForm key={data.version} data={data} onSave={save} />}

          {data.audit.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">History</h3>
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
                {[...data.audit].reverse().map((entry, i) => (
                  <li key={`${entry.at}-${i}`} className="px-3 py-2 text-sm flex flex-wrap justify-between gap-2">
                    <span>
                      <span className="font-medium text-gray-900">{ACTION_LABEL[entry.action] || entry.action}</span>
                      <span className="text-gray-600">: {describeChange(entry)}. {entry.note}</span>
                    </span>
                    <span className="text-gray-500">{formatDateTime(entry.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompanyCommission;
